import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Chip,
  Box,
  Stack,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
} from "@mui/icons-material";
import { StyledTableContainer } from "./StyledComponents";

const QuestionTable = ({
  questions,
  selectedBatchConfig,
  selectedTempQuestions,
  questionClues = {},
  onToggleSelection,
  onApprove,
  onReject,
}) => {
  return (
    <StyledTableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Select</TableCell>
            <TableCell>Question Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Difficulty</TableCell>
            <TableCell>Mood</TableCell>
            <TableCell>Topic</TableCell>
            <TableCell>Subtopic</TableCell>
            <TableCell>Clues</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {questions.map((question) => (
            <TableRow key={question.temp_question_id}>
              <TableCell>
                <Checkbox
                  checked={selectedTempQuestions.includes(
                    question.temp_question_id
                  )}
                  onChange={() => onToggleSelection(question.temp_question_id)}
                  disabled={question.status === "published"}
                  sx={{ color: "#4CAF50" }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ color: "#fff" }}>
                  {question.question_text}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={question.answer_type_name}
                  size="small"
                  sx={{
                    backgroundColor: "#2196F3",
                    color: "#fff",
                    fontWeight: 500,
                  }}
                />
              </TableCell>
              <TableCell>
                {question.level_name ? (
                  <Chip
                    label={question.level_name}
                    size="small"
                    sx={{
                      backgroundColor: "#FF9800",
                      color: "#fff",
                      fontWeight: 500,
                    }}
                  />
                ) : selectedBatchConfig?.difficulties?.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selectedBatchConfig.difficulties.map((d) => (
                      <Chip
                        key={d.id}
                        label={d.name}
                        size="small"
                        sx={{
                          backgroundColor: "#FF9800",
                          color: "#fff",
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#888" }}>
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {question.mood_name ? (
                  <Chip
                    label={question.mood_name}
                    size="small"
                    sx={{
                      backgroundColor: "#9C27B0",
                      color: "#fff",
                      fontWeight: 500,
                    }}
                  />
                ) : selectedBatchConfig?.moods?.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selectedBatchConfig.moods.map((m) => (
                      <Chip
                        key={m.id}
                        label={m.name}
                        size="small"
                        sx={{
                          backgroundColor: "#9C27B0",
                          color: "#fff",
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#888" }}>
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {question.topic_name ? (
                  <Chip
                    label={question.topic_name}
                    size="small"
                    sx={{
                      backgroundColor: "#00BCD4",
                      color: "#fff",
                      fontWeight: 500,
                    }}
                  />
                ) : selectedBatchConfig?.topics?.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selectedBatchConfig.topics.map((t) => (
                      <Chip
                        key={t.id}
                        label={t.name}
                        size="small"
                        sx={{
                          backgroundColor: "#00BCD4",
                          color: "#fff",
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#888" }}>
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {question.subtopic_name ? (
                  <Chip
                    label={question.subtopic_name}
                    size="small"
                    sx={{
                      backgroundColor: "#009688",
                      color: "#fff",
                      fontWeight: 500,
                    }}
                  />
                ) : selectedBatchConfig?.subtopics?.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selectedBatchConfig.subtopics.map((st) => (
                      <Chip
                        key={st.id}
                        label={st.name}
                        size="small"
                        sx={{
                          backgroundColor: "#009688",
                          color: "#fff",
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#888" }}>
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: "#ccc" }}>
                  {(questionClues[question.temp_question_id] || []).length}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={question.status.replace(/_/g, " ")}
                  size="small"
                  color={
                    question.status === "approved_for_live"
                      ? "success"
                      : question.status === "rejected"
                        ? "error"
                        : question.status === "published"
                          ? "primary"
                          : "default"
                  }
                />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Approve">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onApprove(question.temp_question_id)}
                        disabled={question.status === "approved_for_live"}
                        sx={{ color: "#4CAF50" }}
                      >
                        <ApproveIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Reject">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onReject(question.temp_question_id)}
                        disabled={question.status === "rejected"}
                        sx={{ color: "#f44336" }}
                      >
                        <RejectIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default QuestionTable;
